<template>
  <div class="h-full flex flex-col">
    <div
      v-if="floorplanList.length > 1"
      class="flex-none flex py-4 px-4 w-full overflow-x-auto min-w-0 flex-nowrap"
    >
      <l-tab-list v-model="selectedFloorplan" class="m-auto" :tabs="floorplanTabList" />
    </div>
    <swiper
      class="flex-1 w-full h-full"
      :initial-slide="activeFloorplanIndex"
      :allow-touch-move="false"
      @swiper="onSwiper"
      @slide-change="onSlideChange"
    >
      <swiper-slide
        v-for="floorplan in floorplanList"
        :key="floorplan.id"
        class="!flex sm:px-20 sm:pb-4"
      >
        <floor-plan
          :floorplan-id="floorplan.id"
          :tables="floorplan.tables"
          :mode="mode"
          :selected-tables="selectedTables"
          :blocked-tables="blockedTables"
          :show-actual-status="showActualStatus"
          :context-menu-enabled="contextMenuEnabled"
          @table-selected="$emit('tableSelected', $event)"
          @merge-tabs="$emit('mergeTabs', $event)"
          @assign-table="$emit('assignTable', $event)"
          @swap-tables="$emit('swapTables', $event)"
        />
      </swiper-slide>
    </swiper>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import type { Swiper as SwiperType } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { computed, ref, watch } from 'vue'

import { LTabList } from '@last/core-ui/paprika'

import FloorPlan from '@/modules/floorplan/FloorPlan.vue'
import { useFloorplanStore } from '@/store/floorplan'
import type { TableWithTabs } from '@/types'

import 'swiper/css'

type Props = {
  mode?: 'time' | 'diners' | 'amount' | 'reservation' | 'seats'
  selectedTables?: string[]
  blockedTables?: string[]
  showActualStatus?: boolean
  contextMenuEnabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'time',
  selectedTables: () => [],
  blockedTables: () => [],
  showActualStatus: true,
  contextMenuEnabled: true
})

const floorplanStore = useFloorplanStore()

const { floorplanIds, floorplanList } = storeToRefs(floorplanStore)

defineEmits<{
  tableSelected: [TableWithTabs]
  mergeTabs: [string]
  assignTable: [string[]]
  swapTables: [string]
}>()

const selectedFloorplan = ref(floorplanList.value[0].id)

const floorplanTabList = computed(() => {
  return floorplanList.value.map(floorplan => {
    const tabCount = new Set(floorplan.tables.flatMap(table => table.tabIds)).size

    return {
      id: floorplan.id,
      name: floorplan.name,
      badge: props.showActualStatus && tabCount > 0 ? tabCount : undefined
    }
  })
})

const activeFloorplanIndex = computed(() => {
  const floorplanIndex = floorplanIds.value.indexOf(selectedFloorplan.value)
  return Math.max(floorplanIndex, 0)
})

const swiperInstance = ref<SwiperType | null>(null)

function onSwiper(swiper: SwiperType) {
  swiperInstance.value = swiper
}

function onSlideChange(swiper: SwiperType) {
  selectedFloorplan.value = floorplanIds.value[swiper.activeIndex]
}

watch(activeFloorplanIndex, index => {
  if (swiperInstance.value && swiperInstance.value.activeIndex !== index) {
    swiperInstance.value.slideTo(index)
  }
})
</script>
